import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import GameScreenLayout from "@/components/GameScreenLayout"
import { getCityHallRaidDetail } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { formatCityHallRaidOutcome } from "@/utils/cityHall"

const ROUND_MS = 1600

export default function CityHallRaidBattlePage() {
  const { raidId } = useParams<{ raidId: string }>()
  const [roundIndex, setRoundIndex] = useState(-1)
  const [playing, setPlaying] = useState(false)

  const q = useQuery({
    queryKey: ["city-hall-raid-detail", raidId],
    queryFn: () => getCityHallRaidDetail(raidId!),
    enabled: Boolean(raidId),
  })

  const detail = q.data
  const rounds = detail?.combatLog?.rounds ?? []
  const current =
    roundIndex >= 0 && roundIndex < rounds.length ? rounds[roundIndex] : null

  useEffect(() => {
    if (!playing || rounds.length === 0) return
    setRoundIndex(0)
    let idx = 0
    const timer = window.setInterval(() => {
      idx += 1
      if (idx >= rounds.length) {
        window.clearInterval(timer)
        setPlaying(false)
        return
      }
      setRoundIndex(idx)
    }, ROUND_MS)
    return () => window.clearInterval(timer)
  }, [playing, rounds.length])

  const attackScore =
    current?.attackScoreAfter ??
    detail?.attackScore ??
    detail?.combatLog?.attackScore ??
    0
  const defenseScore =
    current?.defenseScoreAfter ??
    detail?.defenseScore ??
    detail?.combatLog?.defenseScore ??
    0
  const total = Math.max(1, attackScore + defenseScore)
  const attackPct = (attackScore / total) * 100

  return (
    <GameScreenLayout title="Бой рейда" backTo="/play/city-hall/raid">
      {q.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : q.isError ? (
        <p className="text-sm text-red-300">
          {getGameApiErrorMessage(q.error, "Не удалось загрузить бой")}
        </p>
      ) : !detail ? (
        <p className="text-sm text-guardians-muted">Нет данных</p>
      ) : (
        <div className="space-y-4 text-sm">
          <p className="text-guardians-muted">
            {detail.attackerCityTitle ?? "Атака"} →{" "}
            {detail.defenderCityTitle ?? "Защита"} · {detail.phase}
          </p>
          <div className="h-3 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full bg-guardians-accent transition-all duration-500"
              style={{ width: `${attackPct}%` }}
            />
          </div>
          <p>
            Атака {attackScore} — Защита {defenseScore}
          </p>
          {current ? (
            <p className="rounded-xl border border-white/10 bg-white/5 p-3">
              {current.text}
            </p>
          ) : (
            <p className="text-guardians-muted">
              {rounds.length
                ? "Нажмите «Проиграть раунды»"
                : "Лог боя пуст"}
            </p>
          )}
          {detail.outcome ? (
            <p className="text-guardians-accent">
              {formatCityHallRaidOutcome(detail.outcome)}
            </p>
          ) : null}
          {rounds.length > 0 && !playing && roundIndex < 0 ? (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className="rounded-xl bg-guardians-accent px-4 py-2 text-guardians-bg"
            >
              Проиграть раунды
            </button>
          ) : null}
        </div>
      )}
    </GameScreenLayout>
  )
}
